import React, { useState } from "react";
import WelcomeTemplate from "./WelcomeTemplate";
import BookingConfirmationTemplate from "./BookingConfirmationTemplate";
import NewsletterTemplate from "./NewsletterTemplate";
import PasswordResetTemplate from "./PasswordResetTemplate";

interface EmailTemplatesProps {
  className?: string;
}

const EmailTemplates: React.FC<EmailTemplatesProps> = ({ className = "" }) => {
  const [activeTemplate, setActiveTemplate] = useState<"welcome" | "booking" | "newsletter" | "password-reset">(
    "welcome"
  );

  const templates = [
    {
      id: "welcome" as const,
      name: "🎉 Email de Bienvenue",
      description: "Accueil chaleureux pour les nouveaux utilisateurs",
      color: "bg-blue-500",
    },
    {
      id: "booking" as const,
      name: "✅ Confirmation de Réservation",
      description: "Validation détaillée des réservations",
      color: "bg-green-500",
    },
    {
      id: "newsletter" as const,
      name: "📰 Newsletter Hebdomadaire",
      description: "Actualités et promotions EventToBee",
      color: "bg-purple-500",
    },
    {
      id: "password-reset" as const,
      name: "🔐 Réinitialisation Mot de Passe",
      description: "Template sécurisé pour reset password",
      color: "bg-blue-500",
    },
  ];

  const renderTemplate = () => {
    switch (activeTemplate) {
      case "welcome":
        return <WelcomeTemplate userName="Marie Dupont" loginUrl="https://eventtobee.com/login" />;
      case "booking":
        return (
          <BookingConfirmationTemplate
            userName="Jean Martin"
            spaceName="Loft industriel Bastille"
            bookingDate="Samedi 25 Novembre 2023"
            bookingTime="14h00 - 22h00"
            bookingId="ETB-2023-11-001"
            totalPrice="480"
            spaceAddress="15 rue de la Roquette, 75011 Paris"
          />
        );
      case "newsletter":
        return <NewsletterTemplate userName="Cher membre EventToBee" />;
      case "password-reset":
        return (
          <PasswordResetTemplate
            userName="Marie Dupont"
            resetUrl="https://eventtobee.com/reset-password?token=abc123xyz"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`email-templates-container ${className}`}>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">📧 Templates d'Emails Modernisés EventToBee</h2>
        <p className="text-gray-600 mb-6">
          Collection de templates d'emails responsive et modernes pour améliorer l'expérience utilisateur.
        </p>

        {/* Navigation des templates */}
        <div className="flex flex-wrap gap-4 mb-6">
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => setActiveTemplate(template.id)}
              className={`flex-1 min-w-[250px] p-4 rounded-xl border-2 transition-all duration-300 ${
                activeTemplate === template.id
                  ? `${template.color} text-white border-transparent shadow-lg transform scale-105`
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:shadow-md"
              }`}
            >
              <div className="font-semibold text-lg mb-1">{template.name}</div>
              <div
                className={`text-sm ${activeTemplate === template.id ? "text-white opacity-90" : "text-gray-500"}`}
              >
                {template.description}
              </div>
            </button>
          ))}
        </div>

        {/* Fonctionnalités */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">✨ Fonctionnalités des Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-green-500 text-xl">📱</span>
              <span className="text-sm font-medium">Responsive Design</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500 text-xl">🎨</span>
              <span className="text-sm font-medium">Design Moderne</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-purple-500 text-xl">⚡</span>
              <span className="text-sm font-medium">Optimisé Performance</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-orange-500 text-xl">🎯</span>
              <span className="text-sm font-medium">Call-to-Action</span>
            </div>
          </div>
        </div>

        {/* Instructions d'utilisation */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-yellow-400 text-xl">💡</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Comment utiliser ces templates</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-2">
                  1. <strong>Cliquez sur "📋 Copier le code HTML"</strong> sous le template choisi
                </p>
                <p className="mb-2">
                  2. <strong>Collez le code</strong> dans votre éditeur d'emails (AdminSendEmails)
                </p>
                <p>
                  3. <strong>Personnalisez</strong> les variables (nom, dates, prix, etc.) selon vos besoins
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Affichage du template actif */}
      <div className="template-preview">{renderTemplate()}</div>

      {/* Statistiques et avantages */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-lg border">
          <div className="text-center">
            <div className="text-3xl mb-2">📈</div>
            <div className="text-2xl font-bold text-green-600">+45%</div>
            <div className="text-sm text-gray-600">Taux d'ouverture</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-lg border">
          <div className="text-center">
            <div className="text-3xl mb-2">👆</div>
            <div className="text-2xl font-bold text-blue-600">+32%</div>
            <div className="text-sm text-gray-600">Taux de clic</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-lg border">
          <div className="text-center">
            <div className="text-3xl mb-2">😊</div>
            <div className="text-2xl font-bold text-purple-600">+58%</div>
            <div className="text-sm text-gray-600">Satisfaction client</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;
